import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGTM } from '../../hooks';
import { GTMRemoveFromBagEvent } from '../../hooks/gtm/model/events/GTMRemoveFromBagEvent';
import { Topic, publish } from '../../utils/pub-sub/pub-sub';
import { isServer } from '../../utils/utils';
import { bffDelete } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import { useBasket } from './useBasket';
import type { GTMSourceEvent } from '../../hooks/gtm/model/events/GTMSourceEvent';
import type { PageSource } from '../../types/page-source';
import type { HttpParams } from '../../utils/url-utils';
import type { Basket, ProductItem } from './model';

interface Props {
    itemId: string;
    productItem: ProductItem;
}

export const useDeleteProductItem = (
    source: GTMSourceEvent,
    pageSource?: PageSource,
): UseMutationResult<Basket, unknown, Props> => {
    const { data } = useBasket();
    const authContext = useAuthContext();
    const queryClient = useQueryClient();
    const { onRemoveProductFromBag } = useGTM();

    return useMutation<Basket, unknown, Props>({
        mutationFn: async ({ itemId, productItem }: Props) => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();
            return bffDelete(`/baskets/${data?.basketId}/product-items/${itemId}`, {
                basketCustomerId: data?.customerInfo?.customerId,
                body: productItem,
                accessToken: authData.access_token,
                customerId: authData.customer_id,
                httpParams: pageSource ? ([{ pageSource }] as HttpParams) : ([] as HttpParams),
            });
        },
        onSuccess: async (basket, { itemId, productItem }): Promise<void> => {
            publish(Topic.PRODUCT_ITEM_DELETED, { itemId });

            onRemoveProductFromBag(new GTMRemoveFromBagEvent(source, basket, productItem));

            queryClient.setQueryData([BffQueryKey.BASKET], basket);

            await Promise.all([
                queryClient.invalidateQueries([BffQueryKey.BASKET_AVAILABILITY], {
                    exact: false,
                }),
                queryClient.invalidateQueries([BffQueryKey.BASKET_INVENTORY], {
                    exact: false,
                }),
                queryClient.invalidateQueries([BffQueryKey.PAYMENT_METHODS], {
                    exact: false,
                }),
                queryClient.invalidateQueries([BffQueryKey.SHIPPING_METHODS], {
                    exact: false,
                }),
            ]);
        },
    });
};
